import { useCallback, useMemo } from "react";
import banksService from "../../services/api/banksService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { QUERY_CONFIGS } from "../../lib/react-query/config";

export const banksQueryKeys = {
  all: ["banks"],
  list: () => [...banksQueryKeys.all, "list"],
  verify: () => [...banksQueryKeys.all, "verify"],
  // FUTURE RELEASE: Query key for MSISDN verification — re-enable when phone number bank verification is supported
  // verifyMsisdn: () => [...banksQueryKeys.all, "verify-msisdn"],
};

export const useFetchBanks = () => {
  return useQuery({
    queryKey: banksQueryKeys.list(),
    queryFn: banksService.fetchBanks,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useBankOptions = () => {
  const {
    data: banksData,
    isLoading: isBanksLoading,
    isError: isBanksError,
    refetch: refetchBanks,
  } = useFetchBanks();

  const banks = useMemo(() => banksData?.data ?? [], [banksData]);
  const bankOptions = useMemo(
    () => banks.map((bank) => ({ value: bank.id.toString(), label: bank.name })),
    [banks],
  );
  const bankFilter = useCallback(
    ({ options, search }) => {
      const normalized = search.toLowerCase().trim();
      return options.filter((opt) => {
        const matchesName = opt.label.toLowerCase().includes(normalized);
        const bank = banks.find((b) => b.id.toString() === opt.value);
        const matchesCode = bank?.code?.toLowerCase().includes(normalized);
        return matchesName || matchesCode;
      });
    },
    [banks],
  );

  return { banks, bankOptions, bankFilter, isBanksLoading, isBanksError, refetchBanks };
};

export const useVerifyBankAccount = () => {
  return useMutation({
    mutationFn: (payload) => banksService.verifyBankAccount(payload),
    mutationKey: banksQueryKeys.verify(),
    retry: false,
  });
};

// FUTURE RELEASE: React Query mutation for MSISDN bank verification.
// Re-enable when phone number verification is supported in the bank account flow.
// export const useVerifyBankAccountViaMsisdn = () => {
//   return useMutation({
//     mutationFn: (payload) => banksService.verifyBankAccountViaMsisdn(payload),
//     mutationKey: banksQueryKeys.verifyMsisdn(),
//     retry: false,
//   });
// };
